import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  type ConsentState,
  readConsent,
  subscribeConsent,
  writeConsent,
  clearConsent,
} from "./store";
import { clearTrackingCookies } from "./cookies";

type Ctx = {
  consent: ConsentState | null;
  ready: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  setCategories: (c: { analytics: boolean; marketing: boolean }) => void;
  reopen: () => void;
};

const ConsentCtx = createContext<Ctx | null>(null);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [ready, setReady] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    setConsent(readConsent());
    setReady(true);
    return subscribeConsent(setConsent);
  }, []);

  const apply = useCallback((c: { analytics: boolean; marketing: boolean }) => {
    const next = writeConsent(c);
    setConsent(next);
    setPreferencesOpen(false);
    // Withdrawal must actually remove what was set before.
    if (!c.analytics || !c.marketing) clearTrackingCookies();
    return next;
  }, []);

  const acceptAll = useCallback(() => {
    apply({ analytics: true, marketing: true });
  }, [apply]);
  const rejectAll = useCallback(() => {
    apply({ analytics: false, marketing: false });
  }, [apply]);
  const setCategories = useCallback(
    (c: { analytics: boolean; marketing: boolean }) => {
      apply(c);
    },
    [apply],
  );
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);
  const reopen = useCallback(() => {
    clearConsent();
    clearTrackingCookies();
    setConsent(null);
    setPreferencesOpen(false);
  }, []);

  return (
    <ConsentCtx.Provider
      value={{
        consent,
        ready,
        preferencesOpen,
        openPreferences,
        closePreferences,
        acceptAll,
        rejectAll,
        setCategories,
        reopen,
      }}
    >
      {children}
    </ConsentCtx.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentCtx);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
